import axios from 'axios'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { extractErrorMessage } from '../utils/apitools'
import useAccount from './useAccount'
import useSnackbar from './useSnackbar'
import { API_BASE_URL } from '../constants/system'
import { Persona, PersonasListResponse } from '../types/gotypes'

/** The built-in personas, which every picker offers even before the library
 *  loads (or if it fails): the server's standard personas, in its order. */
export const STANDARD_PERSONA_NAMES = ['Table Analyst', 'Executive Summarizer']

export interface PickerPersona {
  name: string
  prompt: string
}

export interface PersonaDraft {
  name: string
  prompt: string
}

/**
 * The personas a picker offers (#1523): Company-level (the standard ones
 * first), then the viewer's own — never a colleague's. One entry per name;
 * where a name repeats, it keeps its first position and takes the later
 * prompt, which is the rule the server's resolver applies at run time.
 */
export function pickerPersonas(data: Partial<PersonasListResponse> | undefined): PickerPersona[] {
  if (!data) return STANDARD_PERSONA_NAMES.map((name) => ({ name, prompt: '' }))
  // A server from before the library answers with the flat list only; use it
  // rather than an empty picker while a deploy rolls out.
  if (!data.exec) return (data.personas ?? []).map(({ name, prompt }) => ({ name, prompt }))
  const out: PickerPersona[] = []
  const positions = new Map<string, number>()
  for (const p of [...data.exec, ...(data.mine ?? [])]) {
    const at = positions.get(p.name)
    if (at !== undefined) {
      out[at] = { name: p.name, prompt: p.prompt }
      continue
    }
    positions.set(p.name, out.length)
    out.push({ name: p.name, prompt: p.prompt })
  }
  return out
}

/**
 * A customer's personas library (#1523). Personas used to come from a job's
 * merged config, so they could not be seen outside that job; they are
 * customer-wide now. Read by every persona picker and by Admin → Personas,
 * which also writes through it. All share one query, so an edit in the
 * library reaches the pickers without a reload.
 */
export const usePersonas = ({ customer, enabled = true }: { customer: string; enabled?: boolean }) => {
  const account = useAccount()
  const snackbar = useSnackbar()
  const queryClient = useQueryClient()
  const listKey = ['personas', customer]
  const base = `${API_BASE_URL}/personas/${encodeURIComponent(customer)}`

  const listQuery = useQuery({
    queryKey: listKey,
    enabled: Boolean(enabled && customer && account.user),
    queryFn: async () => {
      try {
        return (await axios.get<PersonasListResponse>(base)).data
      } catch (error) {
        throw new Error(extractErrorMessage(error))
      }
    },
  })

  const invalidate = () => queryClient.invalidateQueries({ queryKey: listKey })
  const onError = (error: Error) => snackbar.error(extractErrorMessage(error))
  const succeeded = (message: string) => () => { invalidate(); snackbar.success(message) }

  const createMutation = useMutation({
    mutationFn: async (draft: PersonaDraft) => (await axios.post<Persona>(base, draft)).data,
    onSuccess: succeeded('Persona saved'),
    onError,
  })
  const updateMutation = useMutation({
    mutationFn: async ({ id, ...draft }: PersonaDraft & { id: string }) => (await axios.put<Persona>(`${base}/${id}`, draft)).data,
    onSuccess: succeeded('Persona saved'),
    onError,
  })
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => { await axios.delete(`${base}/${id}`) },
    onSuccess: succeeded('Persona deleted'),
    onError,
  })
  const promoteMutation = useMutation({
    mutationFn: async (id: string) => (await axios.post<Persona>(`${base}/${id}/promote`)).data,
    onSuccess: succeeded('Persona is now Company-level'),
    onError,
  })
  const demoteMutation = useMutation({
    mutationFn: async (id: string) => (await axios.delete<Persona>(`${base}/${id}/promote`)).data,
    onSuccess: succeeded('Persona is no longer Company-level'),
    onError,
  })
  const copyMutation = useMutation({
    mutationFn: async (id: string) => (await axios.post<Persona>(`${base}/${id}/copy`)).data,
    onSuccess: succeeded('Copy saved to My personas'),
    onError,
  })

  const picker = pickerPersonas(listQuery.data)

  return {
    exec: listQuery.data?.exec ?? [],
    mine: listQuery.data?.mine ?? [],
    colleagues: listQuery.data?.colleagues ?? [],
    /** Company-level then Mine, one per name — what every picker lists. */
    pickerPersonas: picker,
    personaNames: picker.map((p) => p.name),
    isLoading: listQuery.isLoading,
    error: listQuery.error ? listQuery.error.message : null,
    handleCreate: createMutation.mutateAsync,
    handleUpdate: updateMutation.mutateAsync,
    handleDelete: deleteMutation.mutateAsync,
    handlePromote: promoteMutation.mutateAsync,
    handleDemote: demoteMutation.mutateAsync,
    handleCopy: copyMutation.mutateAsync,
    isSaving: createMutation.isPending || updateMutation.isPending,
  }
}

export default usePersonas
